import { useState, type ReactElement } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/useToast';

import {
  isTerminalStatus,
  useJob,
  useJobs,
  useRestartJob,
  type JobExecutionViewModel,
  type JobStatus,
} from '../api';

function statusVariant(status: JobStatus): 'secondary' | 'destructive' | 'outline' {
  if (status === 'FAILED' || status === 'STOPPED' || status === 'ABANDONED') return 'destructive';
  if (status === 'COMPLETED') return 'secondary';
  return 'outline';
}

function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' });
}

interface JobRowProps {
  readonly job: JobExecutionViewModel;
  readonly canRestart: boolean;
  readonly restartLabel: string;
  readonly onRestart: (job: JobExecutionViewModel) => void;
  readonly onSelect: (job: JobExecutionViewModel) => void;
  readonly detailsLabel: string;
  readonly restarting: boolean;
}

function JobRow({
  job,
  canRestart,
  restartLabel,
  onRestart,
  onSelect,
  detailsLabel,
  restarting,
}: JobRowProps): ReactElement {
  const running = !isTerminalStatus(job.status);
  const showRestart =
    canRestart && (job.status === 'FAILED' || job.status === 'STOPPED');

  return (
    <tr
      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
      onClick={() => { onSelect(job); }}
      role="button"
      tabIndex={0}
      aria-label={detailsLabel}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(job);
        }
      }}
    >
      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">#{job.jobId}</td>
      <td className="px-4 py-2 text-sm">{job.jobName}</td>
      <td className="px-4 py-2">
        <Badge variant={statusVariant(job.status)} className="whitespace-nowrap">
          {running && <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" />}
          {job.status}
        </Badge>
        {job.status === 'FAILED' && job.exitDescription.length > 0 && (
          <p className="mt-1 text-xs text-destructive leading-tight max-w-xs truncate" title={detailsLabel}>
            {job.exitDescription}
          </p>
        )}
      </td>
      <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
        {formatDateTime(job.startTime)}
      </td>
      <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
        {formatDateTime(job.endTime)}
      </td>
      {canRestart && (
        <td className="px-4 py-2">
          {showRestart && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={restarting}
              onClick={(e) => { e.stopPropagation(); onRestart(job); }}
            >
              <RefreshCw className="mr-1 h-3 w-3" aria-hidden="true" />
              {restartLabel}
            </Button>
          )}
        </td>
      )}
    </tr>
  );
}

/** Modal showing one job's full detail — including the entire (scrollable) failure stack trace. */
function JobDetailsDialog({
  job,
  i18nPrefix,
  onClose,
}: {
  readonly job: JobExecutionViewModel | null;
  readonly i18nPrefix: string;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  // The list endpoint doesn't hydrate Spring Batch job parameters (so `initiatedBy` is absent there);
  // the single-job endpoint does. Fetch it when the dialog is open and prefer its fuller data, falling
  // back to the list row while it loads.
  const full = useJob(job !== null ? Number(job.jobId) : null);
  const detail = full.data ?? job;
  return (
    <Dialog open={job !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl">
        {job !== null && detail !== null && (
          <>
            <DialogHeader>
              <DialogTitle>
                {t(`${i18nPrefix}.details.title`, { name: job.jobName, id: job.jobId })}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {t(`${i18nPrefix}.details.title`, { name: job.jobName, id: job.jobId })}
              </DialogDescription>
            </DialogHeader>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs sm:grid-cols-4">
              <div>
                <dt className="text-muted-foreground">{t(`${i18nPrefix}.details.status`)}</dt>
                <dd className="font-medium">{detail.status}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t(`${i18nPrefix}.details.initiatedBy`)}</dt>
                <dd className="font-medium">{detail.initiatedBy ?? t(`${i18nPrefix}.details.system`)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t(`${i18nPrefix}.details.started`)}</dt>
                <dd className="font-medium">{formatDateTime(detail.startTime)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t(`${i18nPrefix}.details.finished`)}</dt>
                <dd className="font-medium">{formatDateTime(detail.endTime)}</dd>
              </div>
            </dl>
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">
                {t(`${i18nPrefix}.details.stackTrace`)}
              </p>
              {detail.exitDescription.length > 0 ? (
                <pre className="max-h-[50vh] overflow-auto rounded-md border border-border bg-muted/40 p-3 text-[11px] leading-relaxed whitespace-pre font-mono">
                  {detail.exitDescription}
                </pre>
              ) : (
                <p className="text-xs text-muted-foreground">{t(`${i18nPrefix}.details.noError`)}</p>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export interface JobsListPageProps {
  /** Section caption shown below the heading. E.g. "Global jobs" or "This tenant's jobs". */
  readonly caption: string;
  /** Short explanatory note shown below the caption. */
  readonly captionNote: string;
  /** Translation key prefix for headings/errors (e.g. `routes.platform.jobs`). */
  readonly i18nPrefix: string;
  /** Whether the Restart action is available (gated by managePlatform / openAdmin). */
  readonly canRestart: boolean;
}

/**
 * Shared Jobs list page used by both the Platform console and the per-tenant
 * Admin section. Both tabs hit the same context-routed endpoint
 * (`GET /api/v1/jobs`); the backend routes results by the caller's tenant
 * context — the frontend does not branch on plane.
 *
 * The only differences between the two instantiations are the caption text
 * and the restart-permission flag.
 */
export function JobsListPage({ caption, captionNote, i18nPrefix, canRestart }: JobsListPageProps): ReactElement {
  const { t } = useTranslation();
  const jobsQuery = useJobs();
  const restart = useRestartJob();
  const [selectedJob, setSelectedJob] = useState<JobExecutionViewModel | null>(null);

  function handleRestart(job: JobExecutionViewModel): void {
    restart.mutate(Number(job.jobId), {
      onSuccess: (next) => {
        restart.reset();
        toast({
          title: t(`${i18nPrefix}.restarted`, { id: next.jobId }),
          variant: 'success',
        });
      },
      onError: (error) => {
        toast({
          title: t(`${i18nPrefix}.restartError`, { code: getErrorCodeForUi(error) }),
          variant: 'destructive',
        });
      },
    });
  }

  const jobs = jobsQuery.data ?? [];

  return (
    <div className="space-y-4 bg-background px-8 py-5">
      <div>
        <p className="text-sm font-medium">{caption}</p>
        <p className="text-xs text-muted-foreground mt-1">{captionNote}</p>
      </div>

      {jobsQuery.isPending && <JobsListSkeleton />}

      {jobsQuery.isError && (
        <div className="max-w-xl">
          <FetchErrorCallout
            error={jobsQuery.error}
            heading={t(`${i18nPrefix}.error.heading`)}
            message={t(`${i18nPrefix}.error.message`)}
            codeLabel={t(`${i18nPrefix}.error.codeLabel`)}
            onRetry={() => { jobsQuery.refetch().catch(() => undefined); }}
            retryLabel={t(`${i18nPrefix}.error.retry`)}
          />
        </div>
      )}

      {jobsQuery.isSuccess && jobs.length === 0 && (
        <p className="text-sm text-muted-foreground" role="status">
          {t(`${i18nPrefix}.empty`)}
        </p>
      )}

      {jobsQuery.isSuccess && jobs.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm" aria-label={t(`${i18nPrefix}.heading`)}>
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-xs font-medium text-muted-foreground">
                <th className="px-4 py-2">{t(`${i18nPrefix}.table.id`)}</th>
                <th className="px-4 py-2">{t(`${i18nPrefix}.table.name`)}</th>
                <th className="px-4 py-2">{t(`${i18nPrefix}.table.status`)}</th>
                <th className="px-4 py-2">{t(`${i18nPrefix}.table.started`)}</th>
                <th className="px-4 py-2">{t(`${i18nPrefix}.table.finished`)}</th>
                {canRestart && <th className="px-4 py-2" />}
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <JobRow
                  key={job.jobId}
                  job={job}
                  canRestart={canRestart}
                  restartLabel={t(`${i18nPrefix}.restart`)}
                  onRestart={handleRestart}
                  onSelect={setSelectedJob}
                  detailsLabel={t(`${i18nPrefix}.details.open`)}
                  restarting={restart.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <JobDetailsDialog
        job={selectedJob}
        i18nPrefix={i18nPrefix}
        onClose={() => { setSelectedJob(null); }}
      />
    </div>
  );
}

function JobsListSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
